import Immutable from 'seamless-immutable';

import { SINGLE_SELECT_FACET_FIELDS } from 'components/search/FacetAccordion/FacetAccordion.constants';
import { BRAND_NAME_FACET, COLOR_FACET, REAL_BRAND_FACET } from 'helpers/SearchUtils';
import type { FacetValueVariantType } from 'components/search/FacetAccordion/FacetAccordion.types';
import type { AutoCompleteDataType, FacetDataType, FacetValueType, NavigationVersion } from 'types/search';

interface TransformOptions {
  facet: FacetDataType;
  autoComplete?: AutoCompleteDataType;
  navigationVersion?: NavigationVersion;
  shouldPinSelected?: boolean;
}

interface TransformedFacetData {
  groupedValues: Record<string, FacetValueType[]>;
  sortedGroups: string[];
}

/**
 * Transforms facet values through a pipeline of filtering, grouping, and sorting operations.
 *
 * Pipeline steps:
 * 1. Use autocomplete values if available, otherwise use original facet values
 * 2. Filter single-select facets to show only selected value (if any)
 * 3. Group brand facets alphabetically, leave others ungrouped
 * 4. Sort groups with '#' (numbers) first, then alphabetically
 */
export function transformFacetValues({ facet, autoComplete, navigationVersion, shouldPinSelected = false }: TransformOptions): TransformedFacetData {
  const { facetField, values } = facet;

  // Step 1: Get base values (use autocomplete values if available, otherwise use original facet values)
  const baseValues = autoComplete?.[facetField]?.values ?? values;

  // Step 2: Apply facet-specific filtering only for single-select facets
  const filteredValues = applyFacetFiltering({ ...facet, values: baseValues }, navigationVersion);

  // Step 3: Pin selected values to the top (both sub-lists retain original backend sort order)
  const pinnedValues = shouldPinSelected ? pinSelectedValues(filteredValues) : filteredValues;

  // Step 4: Group and sort
  const facetWithFilteredValues = { ...facet, values: pinnedValues };
  const groupedValues = groupFacetValues(facetWithFilteredValues);
  const sortedGroups = sortGroupKeys({ keys: Object.keys(groupedValues) });

  return { groupedValues, sortedGroups };
}

/**
 * Pins selected values to the front of the list while preserving the original
 * backend sort order within each sub-list (selected and unselected).
 */
function pinSelectedValues(values: FacetValueType[]): FacetValueType[] {
  const selected = values.filter(v => v.selected);
  if (selected.length === 0 || selected.length === values.length) {
    return values;
  }
  const unselected = values.filter(v => !v.selected);
  return [...selected, ...unselected];
}

/**
 * Applies filtering logic specific to single-select facets.
 * For v2p2: shows all values (backend returns correct facetUrls).
 * For v2/legacy: shows only the selected value if one exists, otherwise shows all.
 * For multi-select facets: shows all values unchanged.
 */
function applyFacetFiltering(facet: FacetDataType, navigationVersion?: NavigationVersion): FacetValueType[] {
  if (isSingleSelectFacet(facet) && navigationVersion !== 'v2p2') {
    const selectedValue = facet.values.find(value => value.selected);
    return selectedValue ? [selectedValue] : facet.values;
  }
  return facet.values;
}

/**
 * Determines the UI variant (radio, checkbox, button, swatch) for a facet based on its properties.
 */
export function getFacetVariant(facet: FacetDataType, navigationVersion?: NavigationVersion): FacetValueVariantType {
  if (isSingleSelectFacet(facet)) {
    return 'radio';
  }

  if (facet.symbolicSizingField) {
    return 'button';
  }

  if (navigationVersion === 'v2p2' && facet.facetField === COLOR_FACET && facet.values.some(v => v.metadata?.imgUrl)) {
    return 'swatch';
  }

  return 'checkbox';
}

/**
 * Checks if a facet allows only single selection (e.g., Gender, Department).
 */
export function isSingleSelectFacet(facet: FacetDataType): boolean {
  return !!SINGLE_SELECT_FACET_FIELDS[facet.facetField as keyof typeof SINGLE_SELECT_FACET_FIELDS];
}

/**
 * Returns a comma-separated string of selected facet value names, sorted appropriately.
 * Returns null if no values are selected.
 */
export function getSelectedFacetNames(facet: FacetDataType): string | null {
  const selectedValues = facet.values.filter(value => value.selected).map(value => value.displayName);

  if (selectedValues.length === 0) {
    return null;
  }

  const mutableSelections = Immutable.asMutable(selectedValues);

  // Sort numerically if all values are numbers, otherwise sort alphabetically
  const sortedSelections = mutableSelections.sort((a, b) => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    return a.localeCompare(b);
  });

  return sortedSelections.join(', ');
}

/**
 * Returns the count of selected facet values.
 */
export function getSelectedFacetCount(facet: FacetDataType): number {
  return facet.values.filter(value => value.selected).length;
}

/**
 * Groups facet values for display purposes.
 * Brand facets are grouped alphabetically (A, B, C...) with numbers grouped under '#'.
 * All other facets remain ungrouped (single empty string key).
 */
export function groupFacetValues(facet: FacetDataType): Record<string, FacetValueType[]> {
  if (facet.facetField === BRAND_NAME_FACET || facet.facetField === REAL_BRAND_FACET) {
    return facet.values.reduce(
      (acc, value) => {
        const firstChar = value.displayName.charAt(0);
        const groupKey = /\d/.test(firstChar) ? '#' : firstChar.toUpperCase();
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey]!.push(value);
        return acc;
      },
      {} as Record<string, FacetValueType[]>
    );
  }

  return { '': facet.values };
}

/**
 * Sorts group keys alphabetically, with '#' (numbers) always appearing first.
 */
export function sortGroupKeys({ keys }: { keys: string[] }): string[] {
  return keys.sort((a, b) => {
    if (a === '#') return -1;
    if (b === '#') return 1;
    return a.localeCompare(b);
  });
}
